// src/l1packs/russian.js
// Source: "Norwegian speech sounds compared to Russian" (Olaf Husby, NTNU NoW)
//
// Normalizations:
// - Use canonical IPA /ɡ/ (PDF table writes /g/)
// - Use canonical IPA /oː/ (PDF table writes /o:/)
// - PDF sometimes uses "+~" (between + and ~); we map that to "~" because UI supports only +/~/-.
//
// Visualization tags vocabulary (consonants)
// place:* => lips | teeth | ridge | palate | velum
// air:*   => burst | noisy | smooth
// sec:*   => palatalized | velarized
// phon:*  => voiced | devoice-final
// hint:*  => less-aspirated-ru
//
// Added for UI fields:
// - userComment: detailed learner-facing explanation (articulatory + contrast)
// - easyComment: ultra-short “remember this” line (one breath)
// - difficulty: "ok" | "watch" | "new"
//
// Keyword separation (requested):
// - ruKeywords: RU-analogy / RU-transfer risks (palatalized, final-devoicing, less-aspirated-ru…)
// - noKeywords: Pure Norwegian categories (retroflex-series, front-rounded, central-rounded, length-phonemic…)
// - keywords: union of ruKeywords + noKeywords (for search/filters)
//
// Added for vowels:
// - visVowel: ONLY when note is about vowel QUALITY (front/central/back, close/mid/open, rounding).
//             If the note is only about LENGTH (shorter/longer), keep visVowel undefined.
//
// NEW (L2-target mapping):
// - targetIpa: which Norwegian target key to use in src/poses/norwegianTargets.js
// - l1Adjust: small structured “RU tendency” overrides (palatalization, weaker aspiration, final devoicing, RU shorter/longer, etc.)

import { NO_TARGETS } from "../l2packs/norwegianTargets"; 

const union = (...arrs) => Array.from(new Set(arrs.flat().filter(Boolean)));

/**
 * Infer structured RU adjustments from ruKeywords.
 * Keep this intentionally small + stable: it's a hint layer, not a full phonetics model.
 */
function inferL1Adjust(entry) {
  const ru = entry?.ruKeywords ?? [];
  if (!ru.length) return undefined;

  const adj = {};
  const has = (k) => ru.includes(k);

  // --- consonant timing / laryngeal tendencies
  if (has("less-aspirated-ru")) {
    adj.timing = { ...(adj.timing || {}), burst: "weak" };
  }
  if (has("final-devoicing")) {
    adj.timing = { ...(adj.timing || {}), devoicing: "final" };
  }
  if (has("palatalized")) {
    // secondary tongue-body raising toward hard palate
    adj.body = "palatalized";
  }

  // --- vowel timing tendencies
  if (has("ru-shorter")) {
    adj.timing = { ...(adj.timing || {}), length: "shorter-than-no" };
  }
  if (has("ru-longer")) {
    adj.timing = { ...(adj.timing || {}), length: "longer-than-no" };
  }

  // --- qualitative tendencies (renderer may ignore if unsupported)
  if (has("ru-more-front")) {
    adj.vowelQuality = { ...(adj.vowelQuality || {}), backness: "more-front" };
  }

  // --- extra “risk flags” (not articulator targets, but useful metadata)
  if (has("ru-v-is-fricative-ish")) adj.notes = union(adj.notes || [], ["v-too-fricative"]);
  if (has("ru-x-substitution-risk")) adj.notes = union(adj.notes || [], ["substitute-x-for-ç"]);
  if (has("avoid-x-everywhere")) adj.notes = union(adj.notes || [], ["x-everywhere"]);
  if (has("ru-strong-trill-risk")) adj.notes = union(adj.notes || [], ["over-trill"]);
  if (has("centralization-risk")) adj.notes = union(adj.notes || [], ["centralize-vowel"]);
  if (has("merge-risk")) adj.notes = union(adj.notes || [], ["merge-categories"]);
  if (has("ru-more-front")) adj.notes = union(adj.notes || [], ["fronting"]);

  return Object.keys(adj).length ? adj : undefined;
}

/**
 * Adds targetIpa + l1Adjust to each entry.
 * Default targetIpa behavior:
 * - If NO_TARGETS has the same IPA key, use that key.
 * - Otherwise leave undefined (you can later map manually per-entry if needed).
 */
function withTargets(byIpa) {
  const out = {};
  for (const [ipa, entry] of Object.entries(byIpa)) {
    const targetIpa = Object.prototype.hasOwnProperty.call(NO_TARGETS, ipa) ? ipa : undefined;
    const inferred = inferL1Adjust(entry);

    out[ipa] = {
      ...entry,
      targetIpa,
      // allow manual override if you ever add entry.l1Adjust explicitly
      l1Adjust: entry.l1Adjust ?? inferred,
    };
  }
  return out;
}

export const RUSSIAN_PACK = {
  meta: {
    code: "ru",
    name: "Russian",
    source: "NTNU NoW – Norwegian speech sounds compared to Russian (Olaf Husby)",
  },

  byIpa: withTargets({
    // ---- Consonants ----

    p: {
      overlap: "+",
      no: ["park"],
      l1: ["парк"],
      comment: "Less aspirated in RU",
      easyComment: "Norwegian /p/ needs a stronger air burst than Russian.",
      userComment:
        "Bilabial stop. Lips close and release. Norwegian /p/ is aspirated (audible puff). If you use a Russian-like weak burst, Norwegians may hear it closer to /b/.",
      difficulty: "watch",
      ruKeywords: ["less-aspirated-ru"],
      noKeywords: ["aspiration"],
      keywords: union(["air-burst", "may-sound-like-b"], ["less-aspirated-ru"], ["aspiration"]),
      vis: { tags: ["place:lips", "air:burst", "hint:less-aspirated-ru"] },
    },

    b: {
      overlap: "+",
      no: ["boks"],
      l1: ["бокс"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "RU /b/ can devoice at word end; keep voicing in NO when needed.",
      userComment:
        "Bilabial voiced stop. Lips close and vocal folds vibrate. In Russian it may be “soft” (palatalized), and at the end of a word it can lose voicing. Norwegian does not use palatalization as a main contrast, so avoid over-softening.",
      difficulty: "watch",
      ruKeywords: ["palatalized", "final-devoicing"],
      noKeywords: [],
      keywords: union(["voicing"], ["palatalized", "final-devoicing"]),
      vis: { tags: ["place:lips", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    t: {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "±palatalized, less aspirated in RU",
      easyComment: "NO /t/ has more air than RU /t/; don’t make it too “soft”.",
      userComment:
        "Coronal stop. In the Husby-style description, the closure is made at/just behind the upper teeth, then released with a puff of air (aspiration). Russian /t/ can be palatalized and typically has weaker aspiration; using the Russian version can sound closer to /d/ in Norwegian ears.",
      difficulty: "watch",
      ruKeywords: ["palatalized", "less-aspirated-ru"],
      noKeywords: ["aspiration"],
      keywords: union(["air-burst", "may-sound-like-d"], ["palatalized", "less-aspirated-ru"], ["aspiration"]),
      vis: { tags: ["place:teeth", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    d: {
      overlap: "~",
      no: ["doctor"],
      l1: ["доктор"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "Watch RU final devoicing and avoid over-palatalizing in NO.",
      userComment:
        "Coronal voiced stop. In the Husby-style description, the tongue rim is placed behind the upper teeth and released. Russian may add palatalization and often devoices word-finally; Norwegian does not want systematic final devoicing, so keep voicing when it matters.",
      difficulty: "watch",
      ruKeywords: ["palatalized", "final-devoicing"],
      noKeywords: [],
      keywords: union(["voicing"], ["palatalized", "final-devoicing"]),
      vis: { tags: ["place:teeth", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    // Norwegian retroflex series: new category for RU learners
    "ʈ": {
      overlap: "-",
      no: ["(rt) as in: *fort* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex (often /r/ + /t/ merges)",
      easyComment: "In NO, /r + t/ can merge into one retroflex sound.",
      userComment:
        "Retroflex stop: curl the tongue tip up/back so the tongue rim contacts the alveolar ridge, then release with a puff of air (aspiration). Often arises from /r/ + /t/ in many dialects, but aim for one retroflex consonant.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["retroflex", "aspiration", "r-plus-consonant", "merge"],
      keywords: union(["one-sound"], ["retroflex", "aspiration", "r-plus-consonant", "merge"]),
      vis: { tags: ["place:ridge", "air:burst"] },
    },

    "ɖ": {
      overlap: "-",
      no: ["(rd) as in: *gård* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex (often /r/ + /d/ merges)",
      easyComment: "In NO, /r + d/ can become one retroflex sound.",
      userComment:
        "Retroflex voiced stop: curl the tongue tip up/back so the tongue rim contacts the alveolar ridge, then release. Often arises from /r/ + /d/ in many dialects; aim for one merged consonant rather than an /r/ plus /d/ sequence.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["retroflex", "voicing", "r-plus-consonant", "merge"],
      keywords: union([], ["retroflex", "voicing", "r-plus-consonant", "merge"]),
      vis: { tags: ["place:ridge", "phon:voiced", "air:burst"] },
    },

    k: {
      overlap: "~",
      no: ["kakao"],
      l1: ["какао"],
      comment: "±palatalized, less aspirated in RU",
      easyComment: "NO /k/ often needs more air than RU /k/.",
      userComment:
        "Velar stop. Back of tongue closes at the soft palate (velum) then releases. Norwegian /k/ is aspirated; Russian typically has weaker aspiration and may palatalize. A weak Russian-like burst can be heard closer to /ɡ/ in Norwegian.",
      difficulty: "watch",
      ruKeywords: ["palatalized", "less-aspirated-ru"],
      noKeywords: ["aspiration"],
      keywords: union(["air-burst", "may-sound-like-g"], ["palatalized", "less-aspirated-ru"], ["aspiration"]),
      vis: { tags: ["place:velum", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    "ɡ": {
      overlap: "~",
      no: ["granat"],
      l1: ["гранат"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "RU /ɡ/ can devoice at the end; don’t automatically do that in NO.",
      userComment:
        "Velar voiced stop. Back of tongue closes at the soft palate and releases. Russian may palatalize and often devoices word-finally; Norwegian does not want systematic final devoicing, so keep voicing when it matters.",
      difficulty: "watch",
      ruKeywords: ["palatalized", "final-devoicing"],
      noKeywords: [],
      keywords: union(["voicing"], ["palatalized", "final-devoicing"]),
      vis: { tags: ["place:velum", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    m: {
      overlap: "+",
      no: ["masse"],
      l1: ["масса"],
      comment: "",
      easyComment: "Same idea in RU and NO: lips closed, air through the nose.",
      userComment:
        "Bilabial nasal. Lips close but air flows through the nose (continuous airflow).",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: ["nasal"],
      vis: { tags: ["place:lips", "air:smooth"] },
    },

    n: {
      overlap: "~",
      no: ["NATO"],
      l1: ["нато"],
      comment: "±palatalized",
      easyComment: "RU may use a “soft n”; NO usually wants a plain n here.",
      userComment:
        "Coronal nasal. In the Husby-style description, the tongue rim is placed behind the upper teeth and air flows through the nose. Russian may palatalize; Norwegian typically does not want that “soft” coloring here.",
      difficulty: "watch",
      ruKeywords: ["palatalized"],
      noKeywords: [],
      keywords: union(["nasal"], ["palatalized"]),
      vis: { tags: ["place:teeth", "air:smooth", "sec:palatalized"] },
    },

    "ɳ": {
      overlap: "-",
      no: ["(rn) as in: *barn* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex nasal (often /r/ + /n/ merges)",
      easyComment: "NO /r + n/ can merge into one retroflex nasal.",
      userComment:
        "Retroflex nasal: curl tongue tip up/back so the tongue rim contacts the alveolar ridge, and let air pass through the nose. Often corresponds to /r/ + /n/ sequences in many dialects; aim for one consonant.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["retroflex", "nasal", "r-plus-consonant", "merge"],
      keywords: union([], ["retroflex", "nasal", "r-plus-consonant", "merge"]),
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    "ŋ": {
      overlap: "-",
      no: ["sang", "lang"],
      l1: [],
      comment: "No release [g] (don’t say 'ng')",
      easyComment: "NO /ŋ/ is one sound: no extra [g] at the end.",
      userComment:
        "Velar nasal. Back of the tongue contacts the soft palate while air flows through the nose. Avoid adding a released [g] after it (don’t pronounce ‘ng’ as [nɡ]).",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["velar-nasal", "no-final-g", "one-sound"],
      keywords: union([], ["velar-nasal", "no-final-g", "one-sound"]),
      vis: { tags: ["place:velum", "air:smooth"] },
    },

    f: {
      overlap: "+",
      no: ["kafé"],
      l1: ["кафе"],
      comment: "",
      easyComment: "Similar in RU and NO: lower lip + teeth, noisy air.",
      userComment:
        "Labiodental fricative: upper teeth against lower lip, noisy unvoiced airflow.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: ["fricative"],
      vis: { tags: ["place:teeth", "air:noisy"] },
    },

    "ʋ": {
      overlap: "~",
      no: ["vampyr"],
      l1: ["вампир"],
      comment: "May be unvoiced in final position",
      easyComment: "NO /ʋ/ is smoother than RU /v/; watch final devoicing.",
      userComment:
        "Labiodental approximant (smooth airflow, little friction). In the contrast descriptions, /ʋ/ is treated as voiced (vs /f/ unvoiced). It may devoice word-finally in some contexts/varieties; keep it smooth (not a strong fricative).",
      difficulty: "watch",
      ruKeywords: ["ru-v-is-fricative-ish", "final-devoicing"],
      noKeywords: ["approximant"],
      keywords: union(["not-fricative"], ["ru-v-is-fricative-ish", "final-devoicing"], ["approximant"]),
      vis: { tags: ["place:teeth", "air:smooth", "phon:voiced", "phon:devoice-final"] },
    },

    s: {
      overlap: "+",
      no: ["samovar"],
      l1: ["самовар"],
      comment: "",
      easyComment: "Same core sound: narrow gap, noisy air.",
      userComment:
        "Coronal fricative. Noisy airflow through a narrow channel near the front of the mouth.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: ["fricative"],
      vis: { tags: ["place:teeth", "air:noisy"] },
    },

    "ʃ": {
      overlap: "+",
      no: ["sjokolade"],
      l1: ["шоколад"],
      comment: "",
      easyComment: "Like RU /ш/: noisier, farther back than /s/.",
      userComment:
        "In the Husby-style description, /ʃ/ is made with the tongue curled up/back toward the alveolar ridge, leaving a narrow channel for noisy airflow. Similar perceptually to Russian /ш/.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: ["postalveolar/retroflex-ish"],
      keywords: union(["fricative"], ["postalveolar/retroflex-ish"]),
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ç": {
      overlap: "~",
      no: ["kirurg"],
      l1: ["хирург"],
      comment: "Letter <x> before <и>",
      easyComment: "Smile + raise the middle of the tongue: palatal fricative.",
      userComment:
        "Palatal fricative. Middle of the tongue approaches the hard palate and creates noisy airflow. A useful trick: start from /iː/ and narrow the channel until it becomes fricative, then turn off voicing.",
      difficulty: "new",
      ruKeywords: ["ru-x-substitution-risk"],
      noKeywords: ["palatal-fricative"],
      keywords: union(["smile-cue", "front"], ["ru-x-substitution-risk"], ["palatal-fricative"]),
      vis: { tags: ["place:palate", "air:noisy"] },
    },

    j: {
      overlap: "+",
      no: ["jogg"],
      l1: ["йог"],
      comment: "",
      easyComment: "Same idea: a smooth glide toward the palate.",
      userComment:
        "Palatal glide. Like a very quick /i/ with no audible friction.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: ["glide"],
      vis: { tags: ["place:palate", "air:smooth"] },
    },

    h: {
      overlap: "~",
      no: ["hockey"],
      l1: ["хоккей"],
      comment: "Before front vowel ~ [ç], back vowel ~ [x]",
      easyComment: "NO /h/ shifts: front vowels → [ç], back vowels → [x].",
      userComment:
        "A light voiceless fricative-like breath. Often palatal-ish before front vowels and velar-ish before back vowels. Russian speakers may use a strong [x] everywhere or drop /h/—aim for the lighter Norwegian target and the context shift.",
      difficulty: "watch",
      ruKeywords: ["avoid-x-everywhere"],
      noKeywords: ["context-dependent"],
      keywords: union(["palatal-vs-velar"], ["avoid-x-everywhere"], ["context-dependent"]),
      vis: { tags: ["place:velum", "air:noisy", "sec:palatalized"] },
    },

    r: {
      overlap: "+",
      no: ["ris"],
      l1: ["рис"],
      comment: "",
      easyComment: "Norwegian /r/ may be lighter than Russian.",
      userComment:
        "Norwegian tap /r/ is often like a very quick /d/: the tongue tip quickly taps just behind the upper front teeth, then returns. Dialects vary, so don’t over-trill unless that’s the target variety.",
      difficulty: "watch",
      ruKeywords: ["ru-strong-trill-risk"],
      noKeywords: ["r-variation", "tap"],
      keywords: union(["tap-vs-trill"], ["ru-strong-trill-risk"], ["r-variation", "tap"]),
      vis: { tags: ["place:teeth", "air:smooth"] },
    },

    l: {
      overlap: "~",
      no: ["metall"],
      l1: ["металл"],
      comment: "May be velarized, ±palatalized",
      easyComment: "RU often uses darker /l/; NO is often lighter unless ‘dark L’ is intended.",
      userComment:
        "Lateral. In the Husby-style description, the tongue tip is placed behind the upper teeth and air escapes along the sides. Russian often has stronger velarization as a default; Norwegian is often lighter unless explicitly velarized.",
      difficulty: "watch",
      ruKeywords: ["velarized", "palatalized"],
      noKeywords: [],
      keywords: union(["lateral"], ["velarized", "palatalized"]),
      vis: { tags: ["place:teeth", "air:smooth", "sec:velarized", "sec:palatalized"] },
    },

    "ɭ": {
      overlap: "-",
      no: ["(rl) as in: *perl* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex lateral (often /r/ + /l/ merges)",
      easyComment: "NO /r + l/ can merge into one retroflex ‘L-like’ sound.",
      userComment:
        "Retroflex lateral: curl the tongue tip up/back so the tongue rim touches the alveolar ridge, leaving side openings for airflow. Often corresponds to /r/ + /l/ in many dialects; aim for one merged consonant.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["retroflex", "lateral", "r-plus-consonant", "merge"],
      keywords: union([], ["retroflex", "lateral", "r-plus-consonant", "merge"]),
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    // ---- Vowels / diphthongs ----

    i: {
      overlap: "+",
      no: ["risk"],
      l1: ["риск"],
      comment: "",
      easyComment: "Pretty similar in RU and NO.",
      userComment: "Close front vowel. Russian and Norwegian are very similar here.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: [],
    },

    "iː": {
      overlap: "~",
      no: ["ris"],
      l1: ["рис"],
      comment: "RU vowel is shorter than NO",
      easyComment: "In NO, long vs short matters; RU is often shorter.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, length is part of the vowel identity and can change meaning.",
      difficulty: "watch",
      ruKeywords: ["ru-shorter"],
      noKeywords: ["length-phonemic"],
      keywords: union([], ["ru-shorter"], ["length-phonemic"]),
    },

    y: {
      overlap: "-",
      no: ["lyd", "syk"],
      l1: [],
      comment: "Front rounded vowel (no direct RU equivalent)",
      easyComment: "Tongue like /i/ + lips like /u/ (front + rounded).",
      userComment:
        "No direct Russian equivalent. Keep the tongue forward like /i/, but round/protrude the lips (doc-style cue).",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["front-rounded", "new-vowel"],
      keywords: union(["i-plus-u-lips"], ["front-rounded", "new-vowel"]),
      visVowel: {
        no: { curve: { start: { x: 0.35, y: 0.20 }, mid: { x: 0.35, y: 0.20 }, end: { x: 0.35, y: 0.20 } } },
      },
    },

    "yː": {
      overlap: "-",
      no: ["ny", "dyr"],
      l1: [],
      comment: "Long front rounded vowel (no direct RU equivalent)",
      easyComment: "Same as /y/, but hold it longer (NO length contrast).",
      userComment:
        "Same tongue/lip target as /y/ (front + rounded), but longer. Norwegian length is meaningful, so sustaining it matters.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["front-rounded", "new-vowel", "length-phonemic"],
      keywords: union([], ["front-rounded", "new-vowel", "length-phonemic"]),
      visVowel: {
        no: { curve: { start: { x: 0.35, y: 0.20 }, mid: { x: 0.35, y: 0.20 }, end: { x: 0.35, y: 0.20 } } },
      },
    },

    e: {
      overlap: "~",
      no: ["ekko"],
      l1: ["эхо"],
      comment: "RU vowel is more central: [ə]",
      easyComment: "NO /e/ is more front; don’t relax it into RU-like central [ə].",
      userComment:
        "Norwegian /e/ is more front and stable. Russian often uses a more central reduced vowel in similar contexts; avoid relaxing into central [ə]-like quality.",
      difficulty: "watch",
      ruKeywords: ["centralization-risk"],
      noKeywords: [],
      keywords: union(["front-vs-central"], ["centralization-risk"]),
      visVowel: {
        no: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.5, y: 0.25 }, end: { x: 0.73, y: 0.4 } } },
        l1: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.63, y: 0.24 }, end: { x: 0.74, y: 0.41 } } },
      },
    },

    "eː": {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "RU vowel is shorter than NO",
      easyComment: "NO length matters; hold it longer than RU.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, long vs short can change meaning—sustain the vowel.",
      difficulty: "watch",
      ruKeywords: ["ru-shorter"],
      noKeywords: ["length-phonemic"],
      keywords: union([], ["ru-shorter"], ["length-phonemic"]),
    },

    "ø": {
      overlap: "-",
      no: ["sønn", "høst"],
      l1: [],
      comment: "Front rounded mid vowel (no direct RU equivalent)",
      easyComment: "Tongue like /e/ + lips rounded (as for /u/).",
      userComment:
        "No direct Russian equivalent. Keep tongue mid-front (like /e/), then round/protrude lips (doc-style cue). Don’t let tongue drift back toward /o/.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["front-rounded", "new-vowel"],
      keywords: union(["e-plus-rounded-lips"], ["front-rounded", "new-vowel"]),
      visVowel: {
        no: { curve: { start: { x: 0.42, y: 0.35 }, mid: { x: 0.42, y: 0.35 }, end: { x: 0.42, y: 0.35 } } },
      },
    },

    "øː": {
      overlap: "-",
      no: ["høre", "søt"],
      l1: [],
      comment: "Long front rounded mid vowel (no direct RU equivalent)",
      easyComment: "Same as /ø/, but longer (NO length contrast).",
      userComment:
        "Same tongue/lip target as /ø/ (front + rounded), but longer. Norwegian length is meaningful, so sustain it.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["front-rounded", "new-vowel", "length-phonemic"],
      keywords: union([], ["front-rounded", "new-vowel", "length-phonemic"]),
      visVowel: {
        no: { curve: { start: { x: 0.42, y: 0.35 }, mid: { x: 0.42, y: 0.35 }, end: { x: 0.42, y: 0.35 } } },
      },
    },

    "æ": {
      overlap: "-",
      no: ["hær", "bære"],
      l1: [],
      comment: "Open front vowel (no single RU match)",
      easyComment: "More open than /e/ (jaw drops), but tongue stays forward.",
      userComment:
        "Open front vowel. Keep tongue forward like /e/, but drop the jaw more (doc-style). Russian speakers often drift toward /e/ (too close) or /a/ (too back).",
      difficulty: "new",
      ruKeywords: ["merge-risk"],
      noKeywords: ["open-front", "new-vowel"],
      keywords: union(["jaw-drop"], ["merge-risk"], ["open-front", "new-vowel"]),
      visVowel: {
        no: { curve: { start: { x: 0.32, y: 0.62 }, mid: { x: 0.32, y: 0.62 }, end: { x: 0.32, y: 0.62 } } },
      },
    },

    "æː": {
      overlap: "-",
      no: ["være", "lære"],
      l1: [],
      comment: "Long open front vowel",
      easyComment: "Same as /æ/, but longer (NO length contrast).",
      userComment:
        "Same target as /æ/ (open-front), but longer. Sustain it—Norwegian length is part of the category.",
      difficulty: "new",
      ruKeywords: ["merge-risk"],
      noKeywords: ["open-front", "new-vowel", "length-phonemic"],
      keywords: union([], ["merge-risk"], ["open-front", "new-vowel", "length-phonemic"]),
      visVowel: {
        no: { curve: { start: { x: 0.32, y: 0.62 }, mid: { x: 0.32, y: 0.62 }, end: { x: 0.32, y: 0.62 } } },
      },
    },

    "ɑ": {
      overlap: "+",
      no: ["alfabet"],
      l1: ["алфавит"],
      comment: "RU vowel is more front than NO",
      easyComment: "NO /ɑ/ is farther back; keep it back and open.",
      userComment:
        "Open back vowel in Norwegian: jaw lowered, tongue lowered and retracted (doc-style). Russian is often a bit more front; keep the Norwegian target back.",
      difficulty: "watch",
      ruKeywords: ["ru-more-front"],
      noKeywords: [],
      keywords: union(["backness"], ["ru-more-front"]),
      visVowel: {
        l1: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.5, y: 0.25 }, end: { x: 0.73, y: 0.4 } } },
        no: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.63, y: 0.24 }, end: { x: 0.74, y: 0.41 } } },
      },
    },

    "ɑː": {
      overlap: "~",
      no: ["da"],
      l1: ["да"],
      comment: "RU vowel is more front than NO",
      easyComment: "Same back /ɑ/ target, but hold it longer in NO.",
      userComment:
        "Same back open target as /ɑ/, but longer. Norwegian length is meaningful—sustain it and keep the quality stable.",
      difficulty: "watch",
      ruKeywords: ["ru-more-front"],
      noKeywords: ["length-phonemic"],
      keywords: union(["backness"], ["ru-more-front"], ["length-phonemic"]),
      visVowel: {
        l1: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.5, y: 0.25 }, end: { x: 0.73, y: 0.4 } } },
        no: { curve: { start: { x: 0.43, y: 0.40 }, mid: { x: 0.63, y: 0.24 }, end: { x: 0.74, y: 0.41 } } },
      },
    },

    "ʉ": {
      overlap: "-",
      no: ["hus", "full"],
      l1: [],
      comment: "Central rounded vowel (no direct RU equivalent)",
      easyComment: "Rounded lips, but tongue is more forward/central than /u/.",
      userComment:
        "Central rounded vowel: tongue forward/central while lips stay rounded. Doc-style cue: lips rounded but retracted toward the teeth (like just before whistling).",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["central-rounded", "new-vowel"],
      keywords: union(["u-but-forward"], ["central-rounded", "new-vowel"]),
      visVowel: {
        no: { curve: { start: { x: 0.55, y: 0.25 }, mid: { x: 0.55, y: 0.25 }, end: { x: 0.55, y: 0.25 } } },
      },
    },

    "ʉː": {
      overlap: "-",
      no: ["brun", "hus"],
      l1: [],
      comment: "Long central rounded vowel",
      easyComment: "Same /ʉ/ target, but longer in NO.",
      userComment:
        "Same tongue/lip target as /ʉ/, but longer. Norwegian length is meaningful, so sustain it.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["central-rounded", "new-vowel", "length-phonemic"],
      keywords: union([], ["central-rounded", "new-vowel", "length-phonemic"]),
      visVowel: {
        no: { curve: { start: { x: 0.55, y: 0.25 }, mid: { x: 0.55, y: 0.25 }, end: { x: 0.55, y: 0.25 } } },
      },
    },

    u: {
      overlap: "+",
      no: ["punkt"],
      l1: ["пункт"],
      comment: "",
      easyComment: "Fairly similar in RU and NO.",
      userComment: "Back close rounded vowel. Russian and Norwegian are fairly similar here.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: [],
    },

    "uː": {
      overlap: "~",
      no: ["ord"],
      l1: ["ур"],
      comment: "RU vowel is shorter than NO",
      easyComment: "Hold it longer in NO; length can change meaning.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. Norwegian length is meaningful, so sustain the vowel.",
      difficulty: "watch",
      ruKeywords: ["ru-shorter"],
      noKeywords: ["length-phonemic"],
      keywords: union([], ["ru-shorter"], ["length-phonemic"]),
    },

    o: {
      overlap: "+",
      no: ["koks"],
      l1: ["кокс"],
      comment: "",
      easyComment: "Similar in both languages.",
      userComment: "Back mid rounded vowel. Similar in both languages.",
      difficulty: "ok",
      ruKeywords: [],
      noKeywords: [],
      keywords: [],
    },

    "oː": {
      overlap: "~",
      no: ["på"],
      l1: ["по"],
      comment: "RU vowel is shorter than NO",
      easyComment: "Norwegian long /oː/ is longer than Russian.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, length is part of the vowel identity—hold it.",
      difficulty: "watch",
      ruKeywords: ["ru-shorter"],
      noKeywords: ["length-phonemic"],
      keywords: union([], ["ru-shorter"], ["length-phonemic"]),
    },

    // Diphthongs (kept without visVowel for now)

    "ɑi": {
      overlap: "~",
      no: ["mai"],
      l1: ["май"],
      comment: "NO /ɑ/ is more back",
      easyComment: "NO starts this diphthong farther back.",
      userComment:
        "Norwegian starts the diphthong with a more back /ɑ/ and then moves toward /i/.",
      difficulty: "watch",
      ruKeywords: ["ru-more-front"],
      noKeywords: ["diphthong"],
      keywords: union(["back-start"], ["ru-more-front"], ["diphthong"]),
    },

    "æi": {
      overlap: "~",
      no: ["ei"],
      l1: ["эй"],
      comment: "NO starts more open: /æ/",
      easyComment: "NO starts more open (jaw lower) than the RU-like version.",
      userComment:
        "Norwegian starts more open (jaw more open) than the closest Russian version, then moves toward /i/.",
      difficulty: "watch",
      ruKeywords: [],
      noKeywords: ["diphthong", "open-start"],
      keywords: union([], ["diphthong", "open-start"]),
    },

    "øy": {
      overlap: "-",
      no: ["høy", "øy"],
      l1: [],
      comment: "Diphthong with front rounding (hard for RU)",
      easyComment: "Front + rounded movement: expect this to be new.",
      userComment:
        "This diphthong involves front rounded targets that Russian does not have. Learn /y/ and /ø/ feeling first, then practice the glide.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["diphthong", "front-rounded", "new-category"],
      keywords: union([], ["diphthong", "front-rounded", "new-category"]),
    },

    "æʉ": {
      overlap: "-",
      no: ["au? (dialect/word dependent)"],
      l1: [],
      comment: "Diphthong involving central rounding",
      easyComment: "Includes /ʉ/-like central rounding (new for RU).",
      userComment:
        "This diphthong involves a central rounded target (/ʉ/-like), which is a new category for Russian speakers. Learn /ʉ/ first, then add the glide.",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["diphthong", "central-rounded", "new-category"],
      keywords: union([], ["diphthong", "central-rounded", "new-category"]),
    },

    oy: {
      overlap: "+",
      no: ["Roy"],
      l1: ["рой"],
      comment: "RU /o/ is longer than NO",
      easyComment: "Main difference is timing/length.",
      userComment: "Main difference is length: Russian /o/ tends to be longer here.",
      difficulty: "watch",
      ruKeywords: ["ru-longer"],
      noKeywords: [],
      keywords: union(["length"], ["ru-longer"]),
    },

    // NOTE: RU example is vulgar; keep for fidelity OR mask it in the UI layer.
    uy: {
      overlap: "+",
      no: ["hoi"],
      l1: ["хуй"],
      comment: "Rare in NO (NB: RU word is vulgar)",
      easyComment: "UI note: RU example is vulgar; consider masking.",
      userComment:
        "Rare in Norwegian. (UI note: the Russian example is vulgar; consider masking or replacing at render time.)",
      difficulty: "watch",
      ruKeywords: [],
      noKeywords: ["rare"],
      keywords: union(["ui-vulgar"], ["rare"]),
    },

    "ʉy": {
      overlap: "-",
      no: [],
      l1: [],
      comment: "Rare in NO",
      easyComment: "Rare; involves /ʉ/ (central rounded) + /y/ (front rounded).",
      userComment:
        "Rare in Norwegian. If you meet it, it combines two categories new for Russian speakers: /ʉ/ (central rounded) and /y/ (front rounded).",
      difficulty: "new",
      ruKeywords: [],
      noKeywords: ["rare", "central-rounded", "front-rounded", "new-category"],
      keywords: union([], ["rare", "central-rounded", "front-rounded", "new-category"]),
    },
  }),
};
